import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
// import { setAuthToken } from '../services/api';
import Logo from './Logo';
import './Login.css';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [formError, setFormError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { login, user, error: authError } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();

    // Set flag for login page but don't clear token
    useEffect(() => {
        console.debug('[Login] Login page loaded');
        
        // Only set the flag to indicate we're on the login page
        // Do NOT clear the token here - this was causing the issue with refresh
        window.isOnLoginPage = true;
        
        // Check if we have a token and came from a refresh
        const token = localStorage.getItem('token');
        if (token) {
            console.debug('[Login] Token exists on login page load, might be from refresh');
        }
        
        return () => {
            // Clean up when leaving login page
            window.isOnLoginPage = false;
        };
    }, []);

    // Redirect if already logged in
    useEffect(() => {
        if (user) {
            console.debug('[Login] User is authenticated, redirecting...');
            const from = location.state?.from || '/admin/dashboard';
            navigate(from, { replace: true });
        }
    }, [user, navigate, location]);

    const validateForm = () => {
        console.debug('[Login] Validating form...');
        if (!email) {
            setFormError('Email is required');
            return false;
        }
        if (!password) {
            setFormError('Password is required');
            return false;
        }
        if (!/\S+@\S+\.\S+/.test(email)) {
            setFormError('Please enter a valid email address');
            return false;
        }
        return true;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        console.debug('[Login] Form submitted');
        setFormError('');
        setIsLoading(true);

        if (!validateForm()) {
            setIsLoading(false);
            return;
        }

        try {
            console.debug('[Login] Attempting login...');
            const response = await login(email, password);
            console.debug('[Login] Login successful:', { hasToken: !!response?.data?.token });
            
            // Store token in localStorage if provided in response
            if (response?.data?.token) {
                localStorage.setItem('token', response.data.token);
                console.debug('[Login] Token stored in localStorage');
            }
        } catch (err) {
            console.error('[Login] Login failed:', err);
            
            // Handle rate limiting errors specifically
            if (err.response?.status === 429) {
                setFormError('Too many login attempts. Please wait a few minutes before trying again.');
            } else {
                setFormError(err.response?.data?.message || 'Login failed. Please try again.');
            }
        } finally {
            setIsLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    return (
        <div className="login-container">
            <div className="login-card">
                <div className="login-logo">
                    <Logo width={200} />
                </div>
                <h1 className="login-title">Admin Login</h1>
                
                {(formError || authError) && (
                    <div className="login-error">
                        {formError || authError}
                    </div>
                )}
                
                <form onSubmit={handleSubmit} className="login-form">
                    <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Enter your email"
                            autoComplete="email"
                            disabled={isLoading}
                        />
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">Password</label>
                        <div className="password-input-container">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter your password"
                                autoComplete="current-password"
                                disabled={isLoading}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={togglePasswordVisibility}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                                disabled={isLoading}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <button 
                        type="submit" 
                        className="login-button"
                        disabled={isLoading}
                    >
                        {isLoading ? 'Signing in...' : 'Sign In'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default Login; 